/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, size } from '@everlywell/leaves';
import { animated as A, useSpring, config } from 'react-spring';
import { throttle } from 'lodash-es';

import { generateKey } from 'utils/helpers';
import { useScrolledIntoViewOnce, useScrolledIntoView } from 'utils/hooks';
import * as S from './styles';

type ContentWithImage = {
  heading: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
};

type ImageType = {
  src: string;
  altText: string;
};

export interface InsightsSectionProps {
  title: string;
  headline: string;
  listOfContent: ContentWithImage[];
  mobileImages: ImageType[];
  desktopImages: ImageType[];
}

// funtion for parallax effect in mobile
const calc = (o: any) => `translateY(${o * 0.075}px)`;

export const InsightsSection: React.FC<InsightsSectionProps> = ({
  title,
  headline,
  listOfContent,
  mobileImages,
  desktopImages,
}) => {
  // state for triggering animation springs
  const [titleInView, setTitleInView] = useState<boolean>(false);
  const [headlineInView, setHeadlineInView] = useState<boolean>(false);
  const [cardsInView, setCardsInView] = useState<boolean>(false);
  const [mobileImageInView, setMobileImageInView] = useState<boolean>(false);
  const [mobileImageOffset, setMobileImageOffset] = useState<number>(0);

  // Animation Refs
  const sectionRef = useRef<HTMLDivElement>(null);
  const { inView: sectionInView } = useScrolledIntoView(sectionRef);

  const titleRef = useScrolledIntoViewOnce(() => {
    setTitleInView(true);
  });
  const headlineRef = useScrolledIntoViewOnce(() => {
    setHeadlineInView(true);
  });

  const mobileImageRef = useScrolledIntoViewOnce(() => {
    setMobileImageInView(true);
  });

  const cardsRef = useScrolledIntoViewOnce(() => {
    setCardsInView(true);
  });

  const [{ offset }, set] = useSpring(() => ({ offset: 0 }));

  // create parallax effect for desktop images onScroll
  const handleScroll: () => void = () => {
    if (!sectionRef || !sectionRef.current) {
      return;
    }
    const {
      bottom: sectionBottom,
    } = sectionRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const offset = windowHeight - sectionBottom;
    set({ offset });
  };

  const handleScrollThrottled = throttle(handleScroll, 25);

  // attach the parallax scroll event only when this section is in view
  useEffect(() => {
    if (sectionRef && sectionInView) {
      window.addEventListener('scroll', handleScrollThrottled);
    } else {
      window.removeEventListener('scroll', handleScrollThrottled);
    }
    return () => {
      window.removeEventListener('scroll', handleScrollThrottled);
    };
  }, [sectionRef, sectionInView]);

  // get the mobileImage's height and set for use in mobileImageTransition spring
  useEffect(() => {
    if (mobileImageRef && mobileImageRef.current) {
      setMobileImageOffset(mobileImageRef.current.clientHeight);
    }
  }, [mobileImageRef]);

  // triggered from scrolling into view
  const titleTransition = useSpring({
    opacity: titleInView ? 1 : 0,
    transform: titleInView ? 'translateY(0)' : `translateY(${size.xl1}px)`,
    config: config.slow,
  });

  // triggered from scrolling into view
  const headlineTransition = useSpring({
    opacity: headlineInView ? 1 : 0,
    transform: headlineInView ? 'translateY(0)' : `translateY(${size.xl2}px)`,
    config: config.slow,
  });

  // triggered from scrolling into view
  const cardsTransition = useSpring({
    opacity: cardsInView ? 1 : 0,
    transform: cardsInView ? 'translateY(0)' : `translateY(${size.xl3}px)`,
    config: config.slow,
  });

  // triggered from scrolling into view
  const mobileImageTransition = useSpring({
    transform: mobileImageInView
      ? 'translateY(0)'
      : `translateY(${mobileImageOffset}px)`,
    config: config.slow,
  });

  return (
    <div ref={sectionRef}>
      <S.Container>
        <Row center="xs">
          <Col xs={12} md={8} lg={6}>
            <div ref={titleRef}>
              {/* @ts-ignore */}
              <A.div style={titleTransition}>
                <S.Title text={title} />
              </A.div>
            </div>
          </Col>
        </Row>
        <Row center="xs">
          <Col xs={12} md={8} lg={6}>
            <div ref={headlineRef}>
              {/* @ts-ignore */}
              <A.div style={headlineTransition}>
                <S.Headline text={headline} />
              </A.div>
            </div>
          </Col>
        </Row>
        <Row center="xs" reverse>
          <Col xs={12} md={6}>
            <div ref={cardsRef}>
              <S.InfoBlockContainer>
                {listOfContent.length
                  ? listOfContent.map(content => (
                      // @ts-ignore
                      <A.div style={cardsTransition}>
                        <S.InfoBlock
                          title={content.heading}
                          image={content.imageSrc}
                          imageAlt={content.imageAlt}
                          content={content.description}
                          size="small"
                          key={generateKey()}
                        />
                      </A.div>
                    ))
                  : null}
              </S.InfoBlockContainer>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <S.MainImageContainer ref={mobileImageRef}>
              <S.MainImageContent>
                <S.MobileAnimatedImage
                  style={mobileImageTransition}
                  src={mobileImages[0].src}
                  alt={mobileImages[0].altText}
                />
              </S.MainImageContent>
            </S.MainImageContainer>
            <S.DesktopImageContainer>
              <S.MiddleImage
                aria-hidden="true"
                src={desktopImages[1]?.src}
                alt={desktopImages[1]?.altText}
              />
              <S.TopImage
                aria-hidden="true"
                style={{ transform: offset.interpolate(calc) }}
                src={desktopImages[0].src}
                alt={desktopImages[0].altText}
              />
              {desktopImages[2] ? (
                <S.BottomImage
                  aria-hidden="true"
                  style={{ transform: offset.interpolate(calc) }}
                  src={desktopImages[2].src}
                  alt={desktopImages[2].altText}
                />
              ) : null}
            </S.DesktopImageContainer>
          </Col>
        </Row>
      </S.Container>
    </div>
  );
};

export default InsightsSection;
